import React, { useEffect, useState, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { Flame as IcFlame, Check as IcCheck, Circle as IcCircle } from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';
const CRIMSON = '#8B1A1A';

function Toggle({ checked, onChange, disabled }) {
  return (
    <button
      type="button"
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
      style={{
        position: 'relative', width: 42, height: 24, border: 'none', borderRadius: 12,
        background: checked ? CRIMSON : '#D4D4D4', cursor: disabled ? 'default' : 'pointer',
        transition: 'background 0.15s', padding: 0, opacity: disabled ? 0.5 : 1,
      }}
    >
      <span style={{
        position: 'absolute', top: 3, left: checked ? 21 : 3, width: 18, height: 18,
        background: '#fff', borderRadius: '50%', transition: 'left 0.15s',
        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
      }} />
    </button>
  );
}

function NumField({ label, value, onChange, min, max, suffix, disabled }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>{label}</label>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <input
          type="number"
          value={value ?? ''}
          min={min}
          max={max}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value === '' ? null : parseInt(e.target.value))}
          style={{ width: 90, padding: '7px 10px', border: '1px solid #E2E2E2', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }}
        />
        {suffix && <span style={{ fontSize: 13, color: '#888' }}>{suffix}</span>}
      </div>
    </div>
  );
}

export default function WarmupDashboard() {
  const ctx = useOutletContext() || {};
  const { clientId, isGlobalMode } = ctx;
  const { getToken } = useAuth();

  const [mailboxes, setMailboxes] = useState([]);
  const [network, setNetwork] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  const fetchMailboxes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const url = isGlobalMode
        ? `${API_BASE}/admin/mailboxes`
        : `${API_BASE}/clients/${clientId}/mailboxes`;
      const [mbRes, netRes] = await Promise.all([
        fetch(url, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_BASE}/warmup/network`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (!mbRes.ok) throw new Error(`Mailboxes failed (${mbRes.status})`);
      const data = await mbRes.json();
      setMailboxes(Array.isArray(data) ? data : data.mailboxes || []);
      if (netRes.ok) setNetwork(await netRes.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [clientId, isGlobalMode, getToken]);

  useEffect(() => {
    if (isGlobalMode || clientId) fetchMailboxes();
  }, [isGlobalMode, clientId, fetchMailboxes]);

  const loadSettings = useCallback(async (mb) => {
    setSelectedId(mb.id);
    setSettings(null);
    setSaved(false);
    setLoadingSettings(true);
    try {
      const token = await getToken();
      const r = await fetch(`${API_BASE}/mailboxes/${mb.id}/warmup`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`Warmup load failed (${r.status})`);
      setSettings(await r.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoadingSettings(false);
    }
  }, [getToken]);

  const saveField = useCallback(async (patch) => {
    if (!selectedId) return;
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      const token = await getToken();
      const r = await fetch(`${API_BASE}/mailboxes/${selectedId}/warmup`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(patch),
      });
      if (!r.ok) throw new Error(`Save failed (${r.status})`);
      const updated = await r.json();
      setSettings(updated);
      // Reflect enabled change in mailbox list badge
      if ('enabled' in patch) {
        setMailboxes((prev) => prev.map((m) => m.id === selectedId ? { ...m, warmup_enabled: !!patch.enabled } : m));
      }
      setSaved(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }, [selectedId, getToken]);

  const selected = mailboxes.find((m) => m.id === selectedId);

  return (
    <div>
      <h1 style={{ margin: 0, fontSize: 24, color: '#111' }}>Warmup</h1>
      <p style={{ color: '#666', marginTop: 6, marginBottom: 20, fontSize: 14 }}>
        Build mailbox reputation by sending and replying to small volumes of mail across a network of trusted accounts.
      </p>

      {error && (
        <div style={{ background: '#FDEDED', color: '#b91c1c', padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: 14 }}>
          {error}
        </div>
      )}

      {/* Network stats card */}
      {network && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
          {[
            { label: 'Active in warmup', value: network.total_active || 0 },
            { label: 'Messages today', value: network.messages_today || 0 },
            { label: 'Google', value: network.google || 0 },
            { label: 'Microsoft', value: network.microsoft || 0 },
            { label: 'Zoho', value: network.zoho || 0 },
            { label: 'SMTP', value: network.smtp || 0 },
          ].map((s) => (
            <div key={s.label} style={{ flex: '1 1 130px', minWidth: 130, padding: '14px 16px', border: '1px solid #EEE', borderRadius: 10, background: '#fff' }}>
              <div style={{ fontSize: 12, color: '#888' }}>{s.label}</div>
              <div style={{ fontSize: 22, fontWeight: 600, color: '#111', marginTop: 4 }}>{s.value}</div>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
        {/* Mailbox list */}
        <div style={{ width: 300, flexShrink: 0 }}>
          {loading ? (
            <p style={{ color: '#999', fontSize: 14 }}>Loading…</p>
          ) : mailboxes.length === 0 ? (
            <p style={{ color: '#999', fontSize: 14 }}>No mailboxes found.</p>
          ) : (
            <div style={{ border: '1px solid #EEE', borderRadius: 10, overflow: 'hidden' }}>
              {mailboxes.map((mb) => {
                const isSel = mb.id === selectedId;
                const warm = !!mb.warmup_enabled;
                return (
                  <button
                    key={mb.id}
                    type="button"
                    onClick={() => loadSettings(mb)}
                    style={{
                      display: 'block', width: '100%', textAlign: 'left',
                      padding: '12px 14px', border: 'none', borderBottom: '1px solid #F2F2F2',
                      cursor: 'pointer', background: isSel ? '#F1ECEC' : '#fff',
                    }}
                  >
                    <div style={{ fontSize: 14, fontWeight: isSel ? 600 : 500, color: isSel ? CRIMSON : '#222' }}>
                      {mb.display_name || mb.email_address}
                    </div>
                    <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{mb.email_address}</div>
                    <div style={{ fontSize: 11, marginTop: 4, display: 'inline-flex', alignItems: 'center', gap: 4, color: warm ? '#a16207' : '#bbb' }}>
                      {warm ? <><IcFlame size={11} /> Warming</> : <><IcCircle size={11} /> Off</>}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Settings pane */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {!selected ? (
            <div style={{ padding: '60px 20px', textAlign: 'center', color: '#999', border: '1px dashed #DDD', borderRadius: 12 }}>
              Select a mailbox to configure its warmup.
            </div>
          ) : loadingSettings || !settings ? (
            <p style={{ color: '#999', fontSize: 14 }}>Loading settings…</p>
          ) : (
            <div style={{ border: '1px solid #EEE', borderRadius: 12, padding: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: '#222' }}>{selected.email_address}</div>
                  <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
                    {settings.enabled ? 'Warmup is active' : 'Warmup is off'}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 13, color: '#444' }}>Enable warmup</span>
                  <Toggle checked={!!settings.enabled} onChange={(v) => saveField({ enabled: v })} disabled={saving} />
                </div>
              </div>

              {/* Ramp progress visual */}
              <div style={{ marginBottom: 22 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#666', marginBottom: 6 }}>
                  <span>Current daily volume</span>
                  <span>Target</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 18, fontWeight: 600, color: CRIMSON }}>{settings.daily_volume || 0}</span>
                  <div style={{ flex: 1, height: 8, background: '#F2F2F2', borderRadius: 4, overflow: 'hidden' }}>
                    <div style={{ width: `${Math.min(100, ((settings.daily_volume || 0) / Math.max(1, settings.target_volume || 1)) * 100)}%`, height: '100%', background: CRIMSON, transition: 'width 0.3s' }} />
                  </div>
                  <span style={{ fontSize: 14, color: '#444' }}>{settings.target_volume || 0}/day</span>
                </div>
                {settings.last_ramp_at && (
                  <div style={{ fontSize: 11, color: '#aaa', marginTop: 6 }}>
                    Last ramped {new Date(settings.last_ramp_at).toLocaleDateString()}
                  </div>
                )}
              </div>

              {/* Numeric settings */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 16, marginBottom: 22 }}>
                <NumField
                  label="Daily volume" value={settings.daily_volume} min={1} max={500}
                  onChange={(v) => setSettings({ ...settings, daily_volume: v })}
                  disabled={!settings.enabled || saving}
                />
                <NumField
                  label="Target volume" value={settings.target_volume} min={1} max={500} suffix="/day"
                  onChange={(v) => setSettings({ ...settings, target_volume: v })}
                  disabled={!settings.enabled || saving}
                />
                <NumField
                  label="Ramp step" value={settings.ramp_pct} min={1} max={100} suffix="%"
                  onChange={(v) => setSettings({ ...settings, ramp_pct: v })}
                  disabled={!settings.enabled || saving}
                />
                <NumField
                  label="Reply rate" value={settings.reply_rate_pct} min={0} max={100} suffix="%"
                  onChange={(v) => setSettings({ ...settings, reply_rate_pct: v })}
                  disabled={!settings.enabled || saving}
                />
              </div>

              {/* Behaviour toggles */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 18 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <div style={{ fontSize: 14, color: '#222' }}>Rescue from spam</div>
                    <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>Move warmup messages out of spam to train the inbox classifier.</div>
                  </div>
                  <Toggle checked={!!settings.spam_save_enabled} onChange={(v) => saveField({ spam_save_enabled: v })} disabled={!settings.enabled || saving} />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <div style={{ fontSize: 14, color: '#222' }}>Mark as important</div>
                    <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>Flag warmup messages as important to reinforce trust signals.</div>
                  </div>
                  <Toggle checked={!!settings.mark_important_enabled} onChange={(v) => saveField({ mark_important_enabled: v })} disabled={!settings.enabled || saving} />
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <button
                  type="button"
                  onClick={() => saveField({
                    daily_volume: settings.daily_volume,
                    target_volume: settings.target_volume,
                    ramp_pct: settings.ramp_pct,
                    reply_rate_pct: settings.reply_rate_pct,
                  })}
                  disabled={saving || !settings.enabled}
                  style={{ padding: '9px 18px', border: 'none', borderRadius: 8, cursor: (saving || !settings.enabled) ? 'default' : 'pointer', background: CRIMSON, color: '#fff', fontSize: 14, fontWeight: 600, opacity: (saving || !settings.enabled) ? 0.5 : 1 }}
                >
                  {saving ? 'Saving…' : 'Save numeric settings'}
                </button>
                {saved && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: '#1a7f3c', fontSize: 14 }}>
                    <IcCheck size={16} /> Saved
                  </span>
                )}
                {typeof settings.deliverability_score === 'number' && settings.deliverability_score > 0 && (
                  <span style={{ marginLeft: 'auto', fontSize: 12, color: '#888' }}>
                    Deliverability score: <strong style={{ color: '#222' }}>{settings.deliverability_score}</strong>
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
